import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Clock, FileText, Send,
  Search, ChevronDown, ChevronUp, Users,
} from 'lucide-react';

const ComplianceDashboard = () => {
  const { api } = useAuth();
  const [expiring, setExpiring] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [daysAhead, setDaysAhead] = useState(30);
  const [auditSearch, setAuditSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, auditRes] = await Promise.all([
        api.get(`/compliance/expiring-certifications?days_ahead=${daysAhead}`),
        api.get(`/compliance/audit-log?limit=50${auditSearch ? `&action=${auditSearch}` : ''}`),
      ]);
      setExpiring(expRes.data.expiring || []);
      setAuditLogs(auditRes.data.logs || []);
    } catch (err) {
      if (err.response?.status !== 401) toast.error('Failed to load compliance data');
    }
    finally { setLoading(false); }
  }, [api, daysAhead, auditSearch]);

  useEffect(() => { loadData(); }, [loadData]);

  const sendReminders = async () => {
    setSending(true);
    try {
      const res = await api.post(`/compliance/send-expiry-reminders?days_ahead=${daysAhead}`);
      toast.success(res.data.message);
      loadData();
    } catch { toast.error('Failed to send reminders'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="compliance-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2a3e5d]">Compliance & Audit</h1>
          <p className="text-sm text-slate-500">Training expiry, GDPR compliance, and audit trails</p>
        </div>
      </div>

      {/* Expiring Certifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Expiring Certifications
              {expiring.length > 0 && <Badge className="bg-amber-100 text-amber-700">{expiring.length}</Badge>}
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Days ahead:</span>
                <Input
                  type="number"
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(parseInt(e.target.value) || 30)}
                  className="w-20 h-8"
                  min={1}
                  max={365}
                />
              </div>
              <Button size="sm" onClick={sendReminders} disabled={sending || expiring.length === 0} className="bg-[#20acf6]" data-testid="send-reminders-btn">
                <Send className="h-4 w-4 mr-1" /> {sending ? 'Sending...' : 'Send Reminders'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 text-center py-4">Loading...</p>
          ) : expiring.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Learner</th>
                    <th className="pb-2 pr-4">Course</th>
                    <th className="pb-2 pr-4">Certificate</th>
                    <th className="pb-2 pr-4">Expires</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.map((cert, i) => {
                    const daysLeft = Math.ceil((new Date(cert.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-[#2a3e5d]">{cert.user_name}</p>
                          <p className="text-xs text-slate-500">{cert.user_email}</p>
                        </td>
                        <td className="py-3 pr-4">{cert.course_title}</td>
                        <td className="py-3 pr-4 font-mono text-xs">{cert.certificate_number}</td>
                        <td className="py-3 pr-4">{cert.expires_at?.split('T')[0]}</td>
                        <td className="py-3">
                          <Badge className={daysLeft <= 7 ? 'bg-red-100 text-red-700' : daysLeft <= 14 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                            {daysLeft}d left
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Shield className="h-10 w-10 mx-auto mb-2 text-green-400" />
              <p>No certifications expiring within {daysAhead} days</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Trail */}
      <Card>
        <CardHeader>
          <button onClick={() => setShowAudit(!showAudit)} className="w-full flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Audit Trail
              <Badge variant="outline">{auditLogs.length} entries</Badge>
            </CardTitle>
            {showAudit ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </CardHeader>
        {showAudit && (
          <CardContent>
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  placeholder="Filter by action (login, consent, export...)"
                  className="pl-9 h-9"
                />
              </div>
            </div>
            {auditLogs.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {auditLogs.map((log, i) => (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 text-sm">
                    <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2a3e5d]">{log.action}</p>
                      <p className="text-xs text-slate-500 truncate">{log.user_id} — {log.timestamp}</p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <p className="text-xs text-slate-400 mt-0.5">{JSON.stringify(log.details).slice(0, 100)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No audit logs found</p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ComplianceDashboard;
